import { useQuery } from '@tanstack/react-query';
import { SchoolApi } from '../../../../api/client';
import { StatTile, StatGrid, Term } from '../../../../components/ui';
import ColumnBars from '../../../../components/charts/ColumnBars';
import Spinner from '../../../../components/ui/Spinner';
import ErrorPanel from '../../../../components/ui/ErrorPanel';
import { dayMonth } from '../../lib/cpBits';
import { perDay, studyDelta7vs30, isLowDay } from '../../lib/engagementMath';

interface Props { gradeId: number; sectionId?: number | 'all'; }

/** "38" followed by a small " min" (design `.v` with a 14px unit span). */
const minutes = (n: number) => <>{Math.round(n)}<span className="text-sm"> min</span></>;

export default function EngagementTab({ gradeId, sectionId = 'all' }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics.engagement', gradeId, sectionId],
    queryFn: () => SchoolApi.analyticsEngagement({
      grade: gradeId,
      ...(sectionId !== 'all' ? { section: sectionId } : {}),
    }),
  });

  if (isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (isError || !data) return <ErrorPanel />;

  const dot = data.engagement_dot_counts;
  const total = dot.active + dot.idle + dot.silent;
  const ratio = (v: number) => (total > 0 ? Math.round((v / total) * 100) : 0);
  // The API sends window totals per student; the tiles and the comparison use per-day averages.
  const weeklyDelta = studyDelta7vs30(data.avg_study_minutes_7d, data.avg_study_minutes_30d);
  const series = data.daily_active_series;
  const maxDay = Math.max(0, ...series.map((d) => d.count));

  return (
    <>
      <StatGrid>
        <StatTile label="Active" value={dot.active} sub={`${ratio(dot.active)}% of students`} help="active_students" />
        <StatTile label="Idle" value={dot.idle} sub={`${ratio(dot.idle)}% of students`} help="idle_students" />
        <StatTile label="Silent" value={dot.silent} sub={`${ratio(dot.silent)}% of students`} help="silent_students" />
        <StatTile label="Study min · 7d" value={minutes(perDay(data.avg_study_minutes_7d, 7))}
          sub={weeklyDelta == null ? undefined : (
            <span className={`font-semibold ${weeklyDelta >= 0 ? 'text-success' : 'text-danger-ink'}`}>
              {weeklyDelta >= 0 ? '↑' : '↓'} {Math.abs(weeklyDelta).toFixed(0)}% vs 30-day avg
            </span>
          )}
          help="Average study minutes per student per day over the last 7 days, compared with the 30-day average." />
        <StatTile label="Study min · 30d" value={minutes(perDay(data.avg_study_minutes_30d, 30))} sub="per student, per day"
          help="Average study minutes per student per day over the last 30 days." />
      </StatGrid>
      <h3 className="mb-3 mt-6 font-display text-[15px] font-bold tracking-[-0.01em] text-ink">
        <Term k="daily_active">Daily active students · last 14 days</Term>
      </h3>
      <ColumnBars
        height={150}
        label={`Daily active students: ${series.map((d) => `${dayMonth(d.date)} ${d.count}`).join(', ')}`}
        buckets={series.map((d) => ({
          label: String(Number(d.date.slice(8))),
          value: d.count,
          highlight: isLowDay(d.count, maxDay),
          title: `${dayMonth(d.date)}: ${d.count} active`,
        }))} />
    </>
  );
}
